/**
 * Shared brand styling for the standalone CherryClipz pages (the "R"/"S" look).
 *
 * The flagship (`.ccfp`) and plugin (`.ccpp`) pages each carry their own copy of this token
 * set. Rather than paste it a third and fourth time for the small utility pages, this
 * returns the same system scoped to whatever class you pass — so /plugin-download/ and
 * /contact/ read as part of the same product instead of raw Material UI.
 *
 * Deliberately only the primitives: tokens, nav, buttons, headings, cards. Page-specific
 * layout stays in the page that needs it.
 */

export type BrandTheme = "light" | "dark";

/** Shared with FlagshipPage / PluginPage so the toggle follows you between pages. */
export const BRAND_THEME_KEY = "cc_flagship_theme";

export function brandPageCss(scope: string): string {
  const s = `.${scope}`;
  return `
${s}[data-theme="light"]{
  --bg:#f4f1ee; --surface:#ffffff; --surface-2:#faf7f4; --line:#e9e3dd; --line-2:#ddd5ce;
  --ink:#181315; --muted:#75696e; --faint:#a89ea3; --cherry:#e01e49; --cherry-soft:#ec5578;
  --shadow:0 30px 60px -34px rgba(60,20,30,.28);
}
${s}[data-theme="dark"]{
  --bg:#0e0b0d; --surface:#161114; --surface-2:#1b1418; --line:#282025; --line-2:#38292f;
  --ink:#f5edf0; --muted:#a99ca2; --faint:#7a6d73; --cherry:#ec2b57; --cherry-soft:#ff6f8f;
  --shadow:0 40px 80px -40px rgba(0,0,0,.8);
}
${s}{
  --sans: system-ui,-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --mono: ui-monospace,"SF Mono",Menlo,Consolas,monospace;
  position:relative; min-height:100vh; background:var(--bg); color:var(--ink);
  font-family:var(--sans); line-height:1.5; -webkit-font-smoothing:antialiased;
  transition:background .35s ease, color .35s ease;
}
@media (prefers-reduced-motion: reduce){ ${s}{transition:none} }
${s} *{box-sizing:border-box}
${s} .wrap{max-width:900px;margin:0 auto;padding:0 28px;position:relative}
${s} .mono{font-family:var(--mono)}
${s} .linkbtn{font-family:inherit;background:none;border:none;padding:0;margin:0;cursor:pointer;color:inherit}
${s} .brand-btn{background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;gap:10px}
${s} h1,${s} h2,${s} h3{margin:0;font-weight:770;letter-spacing:-0.03em;line-height:1.05;text-wrap:balance}
${s} .eyebrow{font-family:var(--mono);font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--faint)}
${s} .ink-cherry{color:var(--cherry)}

${s} .btn{display:inline-flex;align-items:center;justify-content:center;gap:9px;font-family:var(--sans);font-weight:650;font-size:15px;border-radius:999px;padding:13px 24px;border:1px solid transparent;cursor:pointer;transition:transform .14s ease, background .2s ease, border-color .2s ease, filter .2s ease;background:transparent;color:var(--ink);text-decoration:none}
${s} .btn-cherry{background:var(--cherry);color:#fff}
${s} .btn-cherry:hover{transform:translateY(-1px);filter:brightness(1.06)}
${s} .btn-cherry:disabled{opacity:.6;cursor:progress;transform:none;filter:none}
${s} .btn-ghost{border-color:var(--line-2);color:var(--ink)}
${s} .btn-ghost:hover{border-color:var(--cherry)}
${s} .btn-lg{font-size:16px;padding:16px 30px}
${s} .btn svg{width:18px;height:18px;stroke:currentColor;fill:none;flex:none;display:block}
@media (prefers-reduced-motion: reduce){ ${s} .btn:hover{transform:none} }
${s} :focus-visible{outline:2px solid var(--cherry);outline-offset:3px;border-radius:8px}

${s} nav{position:sticky;top:0;z-index:20;background:color-mix(in srgb, var(--bg) 82%, transparent);backdrop-filter:blur(12px);border-bottom:1px solid var(--line)}
${s} .nav-in{display:flex;align-items:center;gap:22px;height:70px;max-width:900px;margin:0 auto;padding:0 28px}
${s} .cherry-mark{flex:none;display:block}
${s} .wordmark{font-weight:820;font-size:21px;letter-spacing:-.02em;color:var(--ink)}
${s} .wordmark .ck{color:var(--cherry)}
${s} .nav-right{margin-left:auto;display:flex;align-items:center;gap:16px}
${s} .nav-right .linkbtn{font-size:14px;color:var(--muted);font-weight:550}
${s} .nav-right .linkbtn:hover{color:var(--ink)}
${s} .toggle{display:inline-flex;align-items:center;border:1px solid var(--line-2);border-radius:999px;padding:3px;background:var(--surface-2);cursor:pointer}
${s} .toggle span{display:grid;place-items:center;width:30px;height:26px;border-radius:999px;color:var(--faint)}
${s} .toggle span svg{width:15px;height:15px;stroke:currentColor;fill:none;display:block}
${s}[data-theme="light"] .toggle .sun{background:var(--surface);color:var(--cherry);box-shadow:0 1px 3px rgba(0,0,0,.08)}
${s}[data-theme="dark"] .toggle .moon{background:#241a1f;color:var(--cherry-soft)}
@media (max-width:640px){ ${s} .nav-right .hide-sm{display:none} }

${s} .hero{padding:64px 0 8px;text-align:center}
${s} .hero .eyebrow{display:inline-block;margin-bottom:16px}
${s} .hero h1{font-size:clamp(34px,5vw,54px)}
${s} .hero .sub{max-width:540px;margin:18px auto 0;color:var(--muted);font-size:clamp(15px,1.5vw,17px)}
${s} .hero .cta{display:flex;gap:12px;margin-top:30px;justify-content:center;flex-wrap:wrap}
${s} .hero .fine{margin-top:16px;font-size:12.5px;color:var(--faint)}

${s} section{padding:56px 0;border-top:1px solid var(--line);margin-top:56px}
${s} .sec-head{margin-bottom:28px}
${s} .sec-head h2{font-size:clamp(22px,2.6vw,30px);margin-top:10px}
${s} .sec-head p{color:var(--muted);font-size:15px;margin:10px 0 0}

${s} .card{background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:26px}
${s} .note{border-left:2px solid var(--line-2);padding:2px 0 2px 16px;color:var(--faint);font-size:13.5px;margin-top:28px}
${s} a{color:var(--cherry);text-decoration:none;font-weight:600}
${s} a:hover{text-decoration:underline}

${s} footer{border-top:1px solid var(--line);padding:34px 0;margin-top:56px}
${s} .foot{display:flex;align-items:center;gap:20px;flex-wrap:wrap;font-size:13px;color:var(--faint)}
${s} .foot .fl{margin-left:auto;display:flex;gap:22px;font-family:var(--mono);font-size:12px}
${s} .foot .fl .linkbtn{color:var(--faint)}
${s} .foot .fl .linkbtn:hover{color:var(--muted)}
${s} .foot .tm{flex-basis:100%;margin:0;font-size:12px;line-height:1.5}
`;
}
